import { useZero } from "@rocicorp/zero/react";
import type { ConnectedApp } from "../../shared/generatedDrizzleSchema";
import { sharedMutators } from "../../shared/mutators";
import { useExposeWorkflowAsConnector } from "../hooks/useExposeWorkflowAsConnector";
import { useHasAccess } from "../hooks/useHasAccess";
import { navigate, routes } from "../lib/urls";
import { Icon } from "./Icon";
import { Text } from "./Text";
import { WorkflowFeatureButton } from "./WorkflowFeatureButton";

export function WorkflowConnectorControl({
  workflow,
  connector,
  isLoaded,
  isReadOnly = false,
  target,
}: {
  workflow: { id: string; spaceId: string; name: string };
  connector: Pick<ConnectedApp, "id" | "configuration"> | undefined;
  isLoaded: boolean;
  isReadOnly?: boolean;
  target: string;
}) {
  const z = useZero();
  const canManage = useHasAccess("connected_app:write", connector?.id ?? "");
  const expose = useExposeWorkflowAsConnector({
    workflowId: workflow.id,
    spaceId: workflow.spaceId,
    workflowName: workflow.name,
  });

  const enabled = !!connector && connector.configuration.enabled !== "false";

  return (
    <WorkflowFeatureButton
      target={target}
      label="Workflow as connector"
      icon="api-key-custom"
      isOn={enabled}
      sections={[
        {
          items: connector
            ? [
                {
                  type: "button",
                  left: null,
                  label: "Workflow as connector",
                  subtitle: (
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      style={{ display: "block" }}
                    >
                      Allow calls through this connector. Turning it off keeps
                      its setup and connections.
                    </Text>
                  ),
                  toggled: enabled,
                  disabled: !isLoaded || !canManage || isReadOnly,
                  keepOpen: true,
                  onClick: () => {
                    if (!isLoaded || !canManage || isReadOnly) return;
                    z.mutate(
                      sharedMutators.setWorkflowConnectorEnabled({
                        connectedAppId: connector.id,
                        enabled: !enabled,
                      })
                    );
                  },
                },
                {
                  type: "button",
                  left: null,
                  right: (
                    <Icon
                      name="arrow-up-right"
                      size={16}
                      color="var(--text-secondary)"
                    />
                  ),
                  label: "Open connector settings",
                  onClick: () =>
                    navigate(routes.modal.connector, { id: connector.id }),
                },
              ]
            : [
                {
                  type: "button",
                  left: null,
                  label: "Set up connector",
                  subtitle: (
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      style={{ display: "block" }}
                    >
                      Let chats and other workflows call this workflow through a
                      connector.
                    </Text>
                  ),
                  disabled: !isLoaded || isReadOnly,
                  onClick: () => {
                    if (!isLoaded || isReadOnly) return;
                    const id = expose();
                    if (id) navigate(routes.modal.connector, { id });
                  },
                },
              ],
        },
      ]}
    />
  );
}
